import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Calendar as CalendarIcon, Clock, MapPin, Zap, Check } from "lucide-react";
import { toast } from "sonner";
import { format, addDays, isSameDay } from "date-fns";

const TIMES = ["09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00"];
const EMERGENCY_UPCHARGE = 75; // mirror of backend constant; backend is the source of truth
const DATE_PICKER_DAYS = 30; // show a horizontal scroll of the next 30 days

const to12h = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${m.toString().padStart(2, "0")} ${period}`;
};

export default function BookAppointment() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [service, setService] = useState("");
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const [date, setDate] = useState(today);
    const [time, setTime] = useState("14:00");
    const [address, setAddress] = useState("");
    const [notes, setNotes] = useState("");
    const [emergency, setEmergency] = useState(params.get("emergency") === "1");
    const [designs, setDesigns] = useState([]);
    const [designId, setDesignId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Emergency only makes sense for same-day — if user toggles emergency on, snap date back to today.
    useEffect(() => {
        if (emergency && !isSameDay(date, today)) {
            setDate(today);
        }
    }, [emergency, date, today]);

    useEffect(() => {
        api.get("/services")
            .then((r) => {
                setServices(r.data);
                // honor ?service=...&match=... deep link, else first item
                const sp = params.get("service");
                const match = params.get("match");
                let initial = "";
                if (sp) {
                    const direct = r.data.find((s) => s.service_id === sp);
                    if (direct) initial = direct.service_id;
                }
                if (!initial && match === "dry") initial = "facial_hydrating";
                if (!initial && match === "oily") initial = "facial_signature";
                if (!initial && r.data.length) initial = r.data[0].service_id;
                setService(initial);
            })
            .catch((err) => {
                console.warn("services fetch failed:", err?.message || err);
                toast.error("Couldn't load services menu");
            });
        api.get("/designs")
            .then((r) => setDesigns(r.data))
            .catch((err) => console.warn("designs fetch failed:", err?.message || err));
    }, [params]);

    const selectedService = useMemo(
        () => services.find((s) => s.service_id === service),
        [services, service]
    );

    const grouped = useMemo(() => {
        const order = ["Nails", "Facials", "Tanning", "Makeup", "Hair", "Add-on"];
        const map = new Map();
        services.forEach((s) => {
            if (!map.has(s.category)) map.set(s.category, []);
            map.get(s.category).push(s);
        });
        // Stable ordering
        return order
            .filter((c) => map.has(c))
            .map((c) => [c, map.get(c)])
            .concat([...map.entries()].filter(([c]) => !order.includes(c)));
    }, [services]);

    const total = useMemo(() => {
        const base = selectedService?.price || 0;
        const emg = emergency ? EMERGENCY_UPCHARGE : 0;
        return base + emg;
    }, [selectedService, emergency]);

    const isToday = isSameDay(date, today);
    const pastCutoff = new Date().getHours() >= 18;
    // Only show the same-day-after-6pm warning when the user *is* trying to book today.
    const blockedByCutoff = isToday && !emergency && pastCutoff;

    // Build the date strip (today + next 30 days).
    const dateOptions = useMemo(
        () => Array.from({ length: DATE_PICKER_DAYS + 1 }, (_, i) => addDays(today, i)),
        [today]
    );

    const submit = async (e) => {
        e.preventDefault();
        if (!service) {
            toast.error("Please pick a service");
            return;
        }
        if (!address.trim()) {
            toast.error("Please add your address");
            return;
        }
        if (blockedByCutoff) {
            toast.error("Same-day bookings close at 6pm. Use Emergency for after-hours service or pick a future date.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/bookings", {
                service,
                date: format(date, "yyyy-MM-dd"),
                time,
                address,
                notes,
                emergency,
                design_id: designId || null,
            });
            let successMsg;
            if (emergency) {
                successMsg = "Emergency booking received — we're on the way in 30 min – 1 hour ✨";
            } else if (isToday) {
                successMsg = "Same-day appointment booked — guaranteed ✨";
            } else {
                successMsg = `Booked for ${format(date, "EEE, MMM d")} at ${to12h(time)} ✨`;
            }
            toast.success(successMsg);
            navigate("/portal");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Booking failed";
            toast.error(msg);
        }
        setSubmitting(false);
    };

    return (
        <div className="max-w-[1200px] mx-auto">
            <div className="mb-8">
                <p className="text-xs tracking-[0.3em] uppercase text-rose">Book an appointment</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-2">Pick your day. In your space.</h1>
                <p className="mt-3 text-white/60">
                    Book any day that works for you. Booked before <span className="text-white">6pm today</span>? Same-day is guaranteed.
                    Need us now? Emergency arrives in <span className="text-rose">30 min – 1 hour</span>{" "}
                    (<span className="text-rose">+${EMERGENCY_UPCHARGE} upcharge</span>).
                </p>
            </div>

            {emergency && (
                <div data-testid="emergency-banner" className="mb-8 p-5 rounded-2xl border border-rose/40 bg-rose/10 flex items-center gap-3">
                    <Zap className="w-5 h-5 text-rose animate-pulse" />
                    <div>
                        <p className="font-serif text-lg">Emergency · 30 min – 1 hour</p>
                        <p className="text-sm text-white/70">
                            A pro will arrive in 30 minutes — up to an hour.{" "}
                            <span className="text-rose">+${EMERGENCY_UPCHARGE} upcharge</span> applies. Today only.
                        </p>
                    </div>
                </div>
            )}

            <form onSubmit={submit} className="grid lg:grid-cols-2 gap-8">
                {/* LEFT — service menu + design + emergency toggle */}
                <div className="space-y-6">
                    <div className="rounded-2xl border border-white/10 bg-surface p-6">
                        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4">Service Menu</p>
                        {services.length === 0 ? (
                            <p className="text-sm text-white/50">Loading services…</p>
                        ) : (
                            <div className="space-y-5">
                                {grouped.map(([cat, items]) => (
                                    <div key={cat}>
                                        <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">{cat}</p>
                                        <div className="grid sm:grid-cols-2 gap-2">
                                            {items.map((s) => {
                                                const active = service === s.service_id;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={s.service_id}
                                                        data-testid={`service-${s.service_id}`}
                                                        onClick={() => setService(s.service_id)}
                                                        className={`text-left p-3 rounded-xl border transition ${
                                                            active
                                                                ? "border-rose bg-rose/10"
                                                                : "border-white/10 hover:border-white/30"
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <p className="font-serif text-sm leading-tight">{s.name}</p>
                                                            {active && <Check className="w-4 h-4 text-rose shrink-0" />}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 text-xs">
                                                            <span className="text-gold font-medium">${s.price}</span>
                                                            <span className="text-white/40">·</span>
                                                            <span className="text-white/45">{s.duration_min} min</span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-surface p-6">
                        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4">Pick a design (optional)</p>
                        {designs.length === 0 ? (
                            <p className="text-sm text-white/50">
                                Upload images in your gallery and we'll print them at 3,400 DPI.
                            </p>
                        ) : (
                            <div className="grid grid-cols-4 gap-3">
                                {designs.slice(0, 8).map((d) => (
                                    <button
                                        type="button"
                                        key={d.design_id}
                                        data-testid={`pick-design-${d.design_id}`}
                                        onClick={() => setDesignId(designId === d.design_id ? "" : d.design_id)}
                                        className={`aspect-square rounded-xl overflow-hidden border-2 transition ${
                                            designId === d.design_id ? "border-rose" : "border-white/10 hover:border-white/30"
                                        }`}
                                    >
                                        <img src={d.image_b64} alt={d.name} className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border border-rose/30 bg-rose/5 p-6">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={emergency}
                                onChange={(e) => setEmergency(e.target.checked)}
                                data-testid="emergency-toggle"
                                className="mt-1 accent-rose w-4 h-4"
                            />
                            <div>
                                <p className="font-serif text-lg">Emergency · 30 min – 1 hour</p>
                                <p className="text-sm text-white/65">
                                    Need us ASAP? A pro at your door in 30 minutes — up to an hour for a{" "}
                                    <span className="text-rose">+${EMERGENCY_UPCHARGE} upcharge</span>. Today only.
                                </p>
                            </div>
                        </label>
                    </div>
                </div>

                {/* RIGHT — date, time, address, notes, total, submit */}
                <div className="space-y-6">
                    <div className="rounded-2xl border border-white/10 bg-surface p-6">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <p className="text-xs tracking-[0.3em] uppercase text-white/50 flex items-center gap-2">
                                <CalendarIcon className="w-3.5 h-3.5" /> Date
                            </p>
                            {emergency && (
                                <span className="text-[10px] tracking-widest uppercase text-rose">Emergency · today only</span>
                            )}
                        </div>

                        {/* Selected date headline */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className={`px-5 py-4 rounded-2xl border ${isToday ? "border-rose/40 bg-rose/5" : "border-gold/40 bg-gold/5"}`}>
                                <p className={`text-xs uppercase tracking-widest ${isToday ? "text-rose" : "text-gold"}`}>
                                    {isToday ? "Today" : "Booked for"}
                                </p>
                                <p className="font-serif text-3xl mt-1">{format(date, "EEE · MMM d")}</p>
                            </div>
                            <p className="text-sm text-white/55 flex-1">
                                {isToday
                                    ? <>Booked before <span className="text-white">6pm</span>? Same-day is <span className="text-white">guaranteed</span>.</>
                                    : <>You're booked for <span className="text-white">{format(date, "EEEE, MMMM d")}</span>. We'll confirm details before then.</>
                                }
                            </p>
                        </div>

                        {/* Horizontal scroll of next 30 days */}
                        <div
                            data-testid="date-picker"
                            className={`flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x ${emergency ? "opacity-40 pointer-events-none" : ""}`}
                            style={{ scrollbarWidth: "thin" }}
                        >
                            {dateOptions.map((d) => {
                                const selected = isSameDay(d, date);
                                const isTodayPill = isSameDay(d, today);
                                return (
                                    <button
                                        type="button"
                                        key={d.toISOString().slice(0, 10)}
                                        data-testid={`date-${format(d, "yyyy-MM-dd")}`}
                                        onClick={() => setDate(d)}
                                        className={`snap-start shrink-0 w-16 px-2 py-2 rounded-xl border transition text-center ${
                                            selected
                                                ? "border-rose bg-rose/15 text-white"
                                                : "border-white/10 text-white/70 hover:border-white/30"
                                        }`}
                                    >
                                        <p className="text-[10px] uppercase tracking-widest text-white/50">
                                            {isTodayPill ? "Today" : format(d, "EEE")}
                                        </p>
                                        <p className="font-serif text-xl mt-0.5">{format(d, "d")}</p>
                                        <p className="text-[10px] text-white/40">{format(d, "MMM")}</p>
                                    </button>
                                );
                            })}
                        </div>
                        {blockedByCutoff && (
                            <p className="mt-3 text-xs text-rose">
                                Past 6pm — for today only, use Emergency, or pick a future date above.
                            </p>
                        )}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-surface p-6">
                        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4 flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5" /> Time
                        </p>
                        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                            {TIMES.map((t) => (
                                <button
                                    type="button"
                                    key={t}
                                    data-testid={`time-${t}`}
                                    onClick={() => setTime(t)}
                                    className={`px-3 py-2 rounded-lg text-sm transition border ${
                                        time === t ? "border-rose bg-rose/10 text-rose" : "border-white/10 text-white/70 hover:border-white/30"
                                    }`}
                                >
                                    {to12h(t)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-surface p-6 space-y-4">
                        <div>
                            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-2 flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" /> Address
                            </p>
                            <input
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder="123 Glow St, Brooklyn, NY"
                                data-testid="booking-address"
                                className="w-full bg-transparent border border-white/10 focus:border-rose rounded-xl px-4 py-3 outline-none transition"
                                required
                            />
                        </div>
                        <div>
                            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-2">Notes</p>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Buzzer, parking, allergies, design references…"
                                data-testid="booking-notes"
                                rows={3}
                                className="w-full bg-transparent border border-white/10 focus:border-rose rounded-xl px-4 py-3 outline-none transition resize-none"
                            />
                        </div>
                    </div>

                    {/* Total */}
                    {selectedService && (
                        <div data-testid="booking-total" className="rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/10 to-rose/10 p-6">
                            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Summary</p>
                            <div className="flex items-center justify-between text-white/80">
                                <span>{selectedService.name}</span>
                                <span>${selectedService.price.toFixed(2)}</span>
                            </div>
                            {emergency && (
                                <div className="flex items-center justify-between text-rose mt-1">
                                    <span>Emergency · 30 min – 1 hour</span>
                                    <span>+${EMERGENCY_UPCHARGE.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="border-t border-white/10 mt-3 pt-3 flex items-center justify-between">
                                <span className="font-serif text-lg">Total</span>
                                <span className="font-serif text-2xl text-gold">${total.toFixed(2)}</span>
                            </div>
                        </div>
                    )}

                    <button
                        type="submit"
                        data-testid="booking-submit"
                        disabled={submitting || !service}
                        className="w-full px-7 py-4 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium disabled:opacity-50"
                    >
                        {submitting ? "Booking…" : emergency ? "Send Emergency Booking" : "Confirm Appointment"}
                    </button>
                </div>
            </form>
        </div>
    );
}
